import { Grid } from "@mui/material";
import { ChangeEvent, FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { AgencyCode, AgencyId } from "../../../../reducers/states/agency";
import { JogMap } from "../../../../reducers/states/agency/jogs";
import { GetAgencyFilterLabel, WspDistrictAgencyId } from "../../../../reducers/states/agency/wsp";
import { FeedStatusLevel } from "../../../../enums/feed-status";
import { IRootReducer } from "../../../../reducers";
import AgencyFilter from "./agency-filter";
import { useSearchParams } from "react-router-dom";

interface AgencyFiltersProps {
  props: {
    handleAgencySelect: (event: ChangeEvent<HTMLInputElement>) => void;
  };
}

const AgencyFilters: FC<AgencyFiltersProps> = ({ props }) => {
  const feedStatus = useSelector((state: IRootReducer) => state.dispatchEventFeedReducer.feedStatus);
  const userPrefStore = useSelector((state: IRootReducer) => state.userInfoReducer.userPreferences);
  const [agencyFilters, setAgencyFilters] = useState<{ agencyId: AgencyId, label: string, tooltip: string }[]>([]);
  const [searchParams] = useSearchParams();

  const isStatusCheckDisabled = () => {
    const checkFeed = searchParams.get('checkFeed');
    const domain = window.location.origin.split("//")[1]?.split(":")[0]?.toLowerCase();
    const isDisabledForNonProd = ["dev.vcc.systems", "stage.vcc.systems"].includes(domain) && checkFeed !== "t";
    const isDisabledForLocalhost = domain === "localhost" && checkFeed === "f";
    return isDisabledForNonProd || isDisabledForLocalhost;
  }

  function isFeedDown(id: AgencyId) {
    if (isStatusCheckDisabled()) return false;

    let feed = feedStatus.find((f) => f.agency === id);
    if (feed) {
      return feed.status !== FeedStatusLevel.OK;
    }

    // Default to not showing the indicator if we don't have the feed status retrieved
    return false;
  }

  useEffect(() => {
    const tooltips = new Map<AgencyId, string>([
      [AgencyCode.KCM, "Dispatch information for King County Metro"],
      [AgencyCode.SFD, "Dispatch information for Seattle Fire Department"],
      [AgencyCode.SPD, "Dispatch information for Seattle Police Department"],
      [WspDistrictAgencyId.TAC_D1, "WSP dispatch information for Pierce and Thurston counties. (WSP District 1)"],
      [WspDistrictAgencyId.SEA_D2, "WSP dispatch information for King County. (WSP District 2)"],
      [WspDistrictAgencyId.YAK_D3, "WSP dispatch information for Yakima, Benton, Franklin, Walla Walla, Garfield, Columbia and Asotin counties. (WSP District 3)"],
      [WspDistrictAgencyId.SPO_D4, "WSP dispatch information for Adams, Ferry, Lincoln, Pend Oreille, Spokane, Stevens and Whitman counties. (WSP District 4)"],
      [WspDistrictAgencyId.VAN_D5, "WSP dispatch information for Clark, Cowlitz, Lewis, Klickitat, and Skamania counties. (WSP District 5)"],
      [WspDistrictAgencyId.WEN_D6, "WSP dispatch information for Chelan, Douglas, Grant, Kittitas and Okanogan counties as well as the southwest corner of Adams County. (WSP District 6)"],
      [WspDistrictAgencyId.NW_D7, "WSP dispatch information for Snohomish, Skagit, Island, San Juan, and Whatcom counties. (WSP District 7)"],
      [WspDistrictAgencyId.PEN_D8, "WSP dispatch information for Clallam, Grays Harbor, Jefferson, Kitsap, Mason, Pacific and Wahkiakum Counties. (WSP District 8)"],
    ]);

    const filterMap = new Map<string, { agencyId: AgencyId, label: string, tooltip: string }>();
    userPrefStore.jogFilters.forEach((jogId) => {
      const jog = JogMap.get(jogId);
      jog?.agencies.forEach((agency) => {
        if (agency.isFilter) {
          filterMap.set(agency.id, {
            agencyId: agency.id,
            label: GetAgencyFilterLabel(agency.id as string),
            tooltip: tooltips.get(agency.id) || ""
           });
        }
      });
    });

    setAgencyFilters([...filterMap.values()]);

  }, [userPrefStore.jogFilters])

  return (
    <Grid container alignItems="center">
      {agencyFilters.map((filter) => {
        return (
          <Grid item key={filter.agencyId as string}>
            <AgencyFilter
              props={{
                label: filter.label,
                agencyId: filter.agencyId as string,
                selectedFilters: userPrefStore.agencyFilters || [],
                isFeedDown: isFeedDown((filter.agencyId as string).startsWith(AgencyCode.WSP) ? AgencyCode.WSP : filter.agencyId),
                tooltip: filter.tooltip,
                handleAgencySelect: props.handleAgencySelect,
              }}
            />
          </Grid>
        )
      })}
    </Grid>
  );
};

export default AgencyFilters;